// src/components/common/DataTable.jsx
import React, { useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Search, SearchCheck, SearchIcon } from "lucide-react";
import LoadingComponent from "@/components/common/LoadingComponent";
import Pagination from "@/components/common/Pagination";
import SortableHeader from "@/components/common/SortableHeader";
import { Empty, EmptyContent, EmptyMedia } from "../ui/empty";

/**
 * Generic DataTable Component
 *
 * Props:
 * - title: string               — Card heading
 * - description: string         — Card subheading
 * - icon: ReactNode             — Icon next to title
 * - headerActions: ReactNode    — Buttons/controls in header (e.g. "Add" button)
 * - searchTerm: string
 * - onSearchChange: fn(value)
 * - searchPlaceholder: string
 * - showSearch: boolean         — default true
 *
 * - columns: Array<{
 *     key: string,
 *     label: string,
 *     sortable?: boolean,
 *     hidden?: 'sm'|'md'|'lg'|'xl',   — hide below this breakpoint
 *     render?: fn(row) => ReactNode,   — custom cell renderer
 *   }>
 *
 * - rows: Array<object>
 * - rowKey: string              — unique key field name (e.g. "id", "role_id")
 *
 * - sortBy: string
 * - sortOrder: 'ASC'|'DESC'|''   // '' means no sort
 * - onSort: fn(column, order)    // order can be 'ASC', 'DESC', or null
 *
 * - pagination: { currentPage, totalPages, perPage, totalRecords }
 * - onPageChange: fn(page)
 * - onPerPageChange: fn(perPage)
 * - showPagination: boolean     — default true
 * - paginationTitle: string
 *
 * - loading: boolean
 * - emptyMessage: string|ReactNode
 * - emptyIcon: ReactNode
 *
 * - rowActions: fn(row) => Array<{
 *     label: string,
 *     icon?: ReactNode,
 *     onClick: fn(row),
 *     className?: string,
 *     hidden?: boolean,
 *   }>
 *   — if provided, renders direct icon buttons in the last column
 *
 * - showSerialNumber: boolean   — default true, shows # column with serial numbers
 */
const DataTable = ({
  // Header
  title,
  description,
  icon,
  headerActions,

  // Search
  searchTerm = "",
  onSearchChange,
  searchPlaceholder = "Search...",
  showSearch = true,

  // Table
  columns = [],
  rows = [],
  rowKey = "id",

  // Sort
  sortBy = "",
  sortOrder = "", // Can be 'ASC', 'DESC', or ''
  onSort,

  // Pagination
  pagination,
  onPageChange,
  onPerPageChange,
  showPagination = true,
  paginationTitle = "records",

  // State
  loading = false,
  emptyMessage = "No records found.",
  emptyIcon,

  // Row actions
  rowActions,

  // Serial Number
  showSerialNumber = true,
}) => {
  const hiddenClass = {
    sm: "hidden sm:table-cell",
    md: "hidden md:table-cell",
    lg: "hidden lg:table-cell",
    xl: "hidden xl:table-cell",
  };

  // Calculate serial number offset based on current page
  const serialOffset =
    pagination && pagination.currentPage && pagination.perPage
      ? (pagination.currentPage - 1) * pagination.perPage
      : 0;

  // Handle sort with three states: ASC -> DESC -> no sort
  const handleSort = useCallback((column) => {
    if (!onSort) return;

    let newSortBy = column;
    let newSortOrder = 'ASC';

    // If already sorting by this column
    if (sortBy === column) {
      if (sortOrder === 'ASC') {
        newSortOrder = 'DESC';
      } else if (sortOrder === 'DESC') {
        // Third click: remove sort
        newSortBy = '';
        newSortOrder = '';
      }
    }

    onSort(newSortBy, newSortOrder);
  }, [sortBy, sortOrder, onSort]);

  return (
    <div className="flex flex-col h-[calc(100vh-var(--header-height,64px))] overflow-hidden p-2 sm:p-3">
      <Card className="flex flex-col flex-1 min-h-0 overflow-hidden py-3">
        {/* ── Header ── */}
        <CardHeader className="shrink-0 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between px-3 sm:px-4">
          <div>
            <CardTitle className="text-lg sm:text-xl flex items-center gap-2">
              {icon}
              {title}
            </CardTitle>
            {description && (
              <CardDescription className="mt-1 text-sm">{description}</CardDescription>
            )}
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 w-full lg:w-auto">
            {showSearch && onSearchChange && (
              <div className="relative w-full sm:w-56 lg:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => onSearchChange(e.target.value)}
                  autoComplete="off"
                  className="pl-9 w-full h-9 py-1"
                />
              </div>
            )}
            {headerActions}
          </div>
        </CardHeader>

        {/* ── Content ── */}
        <CardContent className="flex flex-col flex-1 min-h-0 px-2 pb-0">
          {loading ? (
            <div className="flex justify-center items-center flex-1">
              <LoadingComponent />
            </div>
          ) : rows.length === 0 ? (
            <div className="flex-1">
              <Empty>
                <EmptyContent className="flex flex-col justify-center items-center text-muted-foreground text-sm gap-3 py-10">
                  <EmptyMedia>
                    {emptyIcon ? (
                      <emptyIcon className="h-12 w-12" />
                    ) : (
                      <SearchIcon className="h-12 w-12 text-muted-foreground" />
                    )}
                  </EmptyMedia>
                  <p className="text-base font-medium text-foreground">{emptyMessage}</p>
                </EmptyContent>
              </Empty>
            </div>
          ) : (
            <div className="flex flex-col flex-1 min-h-0 gap-0">
              {/* ── Table ── */}
              <div className="rounded-md border flex-1 min-h-0 overflow-hidden">
                <div
                  className="h-full overflow-auto **:data-[slot=table-container]:overflow-visible"
                  style={{
                    scrollbarWidth: "thin",
                    scrollbarColor: "#94a3b8 #f1f5f9"
                  }}
                >
                  <Table>
                    <TableHeader className="sticky top-0 z-20 bg-secondary">
                      <TableRow>
                        {/* ── Serial Number Column Header ── */}
                        {showSerialNumber && (
                          <TableHead className="whitespace-nowrap w-12 text-center text-muted-foreground">
                            #
                          </TableHead>
                        )}

                        {columns.map((col) => (
                          <TableHead
                            key={col.key}
                            className={[
                              "whitespace-nowrap",
                              col.hidden ? hiddenClass[col.hidden] : "",
                            ].join(" ")}
                          >
                            {col.sortable && onSort ? (
                              <SortableHeader
                                column={col.key}
                                sortBy={sortBy}
                                sortOrder={sortOrder}
                                onSort={handleSort}  // Using handleSort instead of onSort directly
                              >
                                {col.label}
                              </SortableHeader>
                            ) : (
                              col.label
                            )}
                          </TableHead>
                        ))}

                        {/* ── Actions column header ── */}
                        {rowActions && (
                          <TableHead className="text-center right-0">
                            Actions
                          </TableHead>
                        )}
                      </TableRow>
                    </TableHeader>

                    <TableBody>
                      {rows.map((row, index) => (
                        <TableRow key={row[rowKey]}>
                          {/* ── Serial Number Cell ── */}
                          {showSerialNumber && (
                            <TableCell className="whitespace-nowrap w-12 text-center text-muted-foreground text-sm">
                              {serialOffset + index + 1}
                            </TableCell>
                          )}

                          {columns.map((col) => (
                            <TableCell
                              key={col.key}
                              className={[
                                "whitespace-nowrap",
                                col.hidden ? hiddenClass[col.hidden] : "",
                                col.className || "",
                              ].join(" ")}
                            >
                              {col.render ? col.render(row) : row[col.key] ?? "—"}
                            </TableCell>
                          ))}

                          {/* ── Row Actions — Direct Icons ── */}
                          {rowActions && (
                            <TableCell className="text-center right-0 z-10 whitespace-nowrap">
                              <div className="flex items-center justify-center gap-1">
                                {rowActions(row)
                                  .filter((action) => !action.hidden)
                                  .map((action, i) => (
                                    <Button
                                      key={i}
                                      variant={action.variant || "ghost"}
                                      size="sm"
                                      className={`h-8 w-8 p-0 ${action.className || ""}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        action.onClick(row);
                                      }}
                                      title={action.label}
                                    >
                                      {action.icon ? (
                                        <span className="h-4 w-4 flex items-center justify-center">
                                          {action.icon}
                                        </span>
                                      ) : (
                                        action.label
                                      )}
                                    </Button>
                                  ))}
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* ── Pagination ── */}
              {showPagination && pagination && (
                <div className="shrink-0 mt-1">
                  <Pagination
                    currentPage={pagination.currentPage}
                    totalPages={pagination.totalPages}
                    perPage={pagination.perPage}
                    totalRecords={pagination.totalRecords}
                    onPageChange={onPageChange}
                    onPerPageChange={onPerPageChange}
                    title={paginationTitle}
                  />
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DataTable;